// Performance monitoring for production
class PerformanceMonitor {
  constructor() {
    this.isProduction = import.meta.env.PROD;
    this.isDevelopment = import.meta.env.DEV || 
      (typeof window !== 'undefined' && 
       (window.location.hostname === 'localhost' || 
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.includes('localhost')));
    this.metrics = {};
    this.observers = [];
  }

  init() {
    if (!this.isProduction || this.isDevelopment) return;

    // Monitor Core Web Vitals
    this.observeLCP();
    this.observeFID();
    this.observeCLS();
    this.observeFCP();
    this.observeTTFB();
  }

  observeLCP() {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      this.metrics.lcp = lastEntry.startTime;
      this.reportMetric('lcp', lastEntry.startTime);
    });

    observer.observe({ entryTypes: ['largest-contentful-paint'] });
    this.observers.push(observer);
  }

  observeFID() {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        this.metrics.fid = entry.processingStart - entry.startTime;
        this.reportMetric('fid', this.metrics.fid);
      });
    });

    observer.observe({ entryTypes: ['first-input'] });
    this.observers.push(observer);
  }

  observeCLS() {
    let clsValue = 0;
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      this.metrics.cls = clsValue;
      this.reportMetric('cls', clsValue);
    });

    observer.observe({ entryTypes: ['layout-shift'] });
    this.observers.push(observer);
  }

  observeFCP() {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        this.metrics.fcp = entry.startTime;
        this.reportMetric('fcp', entry.startTime);
      });
    });

    observer.observe({ entryTypes: ['paint'] });
    this.observers.push(observer);
  }

  observeTTFB() {
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    if (navigationEntry) {
      this.metrics.ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
      this.reportMetric('ttfb', this.metrics.ttfb);
    }
  }

  reportMetric(name, value) {
    if (!this.isProduction) return;
    if (import.meta.env.VITE_ENABLE_PERF !== 'true') return;

    // Send to analytics service
    try {
      // Example: Send to Google Analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'web_vitals', {
          event_category: 'Performance',
          event_label: name,
          value: Math.round(value),
          non_interaction: true
        });
      }

      // Send to your own analytics
      this.sendToAnalytics(name, value);
    } catch (error) {
      console.warn('Failed to report metric:', error);
    }
  }

  sendToAnalytics(name, value) {
    // Skip analytics in development or if endpoint doesn't exist
    if (!this.isProduction || this.isDevelopment) {
      return;
    }
    if (import.meta.env.VITE_ENABLE_PERF !== 'true') return;
    
    // Only send analytics if we're in production and the endpoint exists
    fetch('/api/analytics/performance', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        metric: name,
        value: value,
        timestamp: Date.now(),
        url: window.location.href
      })
    }).catch((error) => {
      // Silently fail for missing endpoints
      console.debug('Analytics endpoint not available:', error.message);
    });
  }

  measureCustomMetric(name, fn) {
    const start = performance.now();
    const result = fn();
    const end = performance.now();
    
    this.metrics[name] = end - start;
    this.reportMetric(name, this.metrics[name]);
    
    return result;
  }

  async measureAsyncMetric(name, fn) {
    const start = performance.now();
    const result = await fn();
    const end = performance.now();
    
    this.metrics[name] = end - start;
    this.reportMetric(name, this.metrics[name]);
    
    return result;
  }

  getMetrics() {
    return { ...this.metrics };
  }

  disconnect() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

export const performanceMonitor = new PerformanceMonitor();

// Initialize performance monitoring only in production
if (typeof window !== 'undefined' && import.meta.env.PROD && !performanceMonitor.isDevelopment) {
  performanceMonitor.init();
}

// React performance utilities
export const withPerformanceTracking = (WrappedComponent, componentName) => {
  return React.memo((props) => {
    const startTime = performance.now();
    
    React.useEffect(() => {
      const endTime = performance.now();
      performanceMonitor.reportMetric(`${componentName}_render`, endTime - startTime);
    });

    return React.createElement(WrappedComponent, props);
  });
};

// Bundle size monitoring
export const logBundleSize = () => {
  if (!performanceMonitor.isProduction) return;

  const resources = performance.getEntriesByType('resource');
  const jsResources = resources.filter(r => r.name.includes('.js'));
  const cssResources = resources.filter(r => r.name.includes('.css'));
  
  const totalJSSize = jsResources.reduce((sum, r) => sum + (r.transferSize || 0), 0);
  const totalCSSSize = cssResources.reduce((sum, r) => sum + (r.transferSize || 0), 0);
  
  performanceMonitor.reportMetric('bundle_js_size', totalJSSize);
  performanceMonitor.reportMetric('bundle_css_size', totalCSSSize);
};

// Initialize bundle size monitoring
if (typeof window !== 'undefined') {
  window.addEventListener('load', logBundleSize);
}

// Export functions for App.jsx
export const initPerformanceMonitoring = () => {
  if (typeof window !== 'undefined' && import.meta.env.PROD) {
    performanceMonitor.init();
  }
};

export const preloadCriticalResources = () => {
  // Only preload in production and when explicitly enabled
  if (typeof window === 'undefined') return;
  if (!import.meta.env.PROD) return;
  if (import.meta.env.VITE_ENABLE_PRELOAD !== 'true') return;

  // Preload critical resources (must point to real URLs in your deployment)
  const criticalResources = [];

  criticalResources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource;
    link.as = 'script';
    document.head.appendChild(link);
  });
};